import React, { FC, FunctionComponent } from "react";

import CheckCircleIcon from "@uui/CheckCircle";

import LoadingButton, { LoadingButtonProps } from "@mui/lab/LoadingButton";
import {
  Chip,
  ChipProps,
  Stack,
  Divider as MuiDivider,
  Typography,
  TypographyProps,
  DividerProps,
} from "@mui/material";
import Paper, { PaperProps } from "@mui/material/Paper";
import styled from "@mui/material/styles/styled";

import { roundedOutline } from "@rui/styles";

export const Divider = styled<FC<DividerProps>>((props) => (
  <MuiDivider {...props} />
))(({ theme }) => ({
  borderColor: theme.palette.shades.neutral[50],
  width: "100%",
}));

export const Container = styled(Stack)(() => ({
  margin: "auto",
  maxWidth: "750px",
  width: "100%",
  padding: "10px 40px 40px",
}));

export const Button = styled<FC<LoadingButtonProps & { primary?: boolean }>>(
  ({ primary: _, ...props }) => <LoadingButton variant="outlined" {...props} />
)(({ theme, primary }) => ({
  backgroundColor: primary ? theme.palette.shades.primary[500] : "white",
  border: "1px solid",
  borderColor: theme.palette.shades.neutral[100],
  borderRadius: theme.corners.base,
  height: "48px",
  fontSize: "1rem",
  marginTop: 0,
  color: primary ? "white" : theme.palette.shades.primary[500],
  minWidth: "auto",
  "&:hover": {
    color: primary ? "#FFF" : theme.palette.shades.primary[600],
    backgroundColor: primary
      ? theme.palette.shades.primary[600]
      : theme.palette.shades.primary[50],
  },
  "&.Mui-disabled": {
    backgroundColor: primary ? theme.palette.shades.primary[50] : undefined,
  },
}));

export const SettingTitle = styled<FC<TypographyProps>>((props) => (
  <Typography variant="h3" {...props} />
))(({ theme }) => ({
  display: "block",
  color: theme.palette.shades.neutral[800],
}));

export const SettingSubtitle = styled<FC<TypographyProps>>((props) => (
  <Typography variant="bodySecondaryReg" {...props} />
))(({ theme }) => ({
  display: "block",
  color: theme.palette.shades.neutral[500],
}));

export const SettingSubtextHeader = styled<FC<TypographyProps>>((props) => (
  <Typography variant="bodyPrimaryMed" {...props} />
))(({ theme }) => ({
  display: "block",
  color: theme.palette.shades.neutral[700],
}));

export const SettingSubtext = styled<FC<TypographyProps>>((props) => (
  <Typography variant="bodyPrimaryReg" {...props} />
))(({ theme }) => ({
  display: "block",
  color: theme.palette.shades.neutral[800],
}));

export const ConnectedBadge = styled<FC<ChipProps>>((props) => (
  <Chip {...props} icon={<CheckCircleIcon />} label="Connected" />
))(({ theme }) => ({
  height: "auto",
  width: "auto",
  borderRadius: "4px",
  backgroundColor: theme.palette.shades.meadow[50],
  "& .MuiChip-label": {
    padding: "1px 2px",
    color: theme.palette.shades.neutral[600],
    fontWeight: 500,
  },
  "& .MuiSvgIcon-root": {
    margin: "0 2px",
    fontSize: "16px",
    color: theme.palette.shades.meadow[600],
  },
}));

type SettingsModuleCardProps = PaperProps & { active?: boolean };

export const SettingsModuleCard = styled<
  FunctionComponent<SettingsModuleCardProps>
>(({ active: _, ...props }) => <Paper {...props} />)(({ active, theme }) => ({
  width: "100%",
  padding: "8px",
  backgroundColor: "#FFF",
  position: "relative",
  borderRadius: "8px",
  margin: "16px 0",
  scrollMarginTop: "32px",
  ...roundedOutline({
    border: active ? "2px solid" : "1px solid",
    borderRadius: "8px",
    borderColor: active
      ? theme.palette.shades.primary[500]
      : theme.palette.shades.neutral[100],
  }),
}));
